import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircleIcon from '@mui/icons-material/Circle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
	DataGrid,
	GridFooter,
	GridToolbarContainer,
	GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { GetSubAccountInfoFn } from '../../../data/apiCalls';
import CustomLoading from '../../shared/CustomLoading';
import useSessionContext from '../../../data/queryHooks/useSessionContext';
import { dateToShortString } from '../../../scripts/dataConversions';
import {
	permissionName,
	permissionOperation,
} from '../../../scripts/pageConstants';
import CustomNoResultsOverlay from '../../shared/CustomNoResultsOverlay';

const linkText = {
	textTransform: 'uppercase',
	fontWeight: 'bold',
};

const CustomToolbar = () => {
	const navigate = useNavigate();
	const { permissions } = useSessionContext();

	return (
		<GridToolbarContainer sx={{ justifyContent: 'space-between', mb: 0.5 }}>
			<Button
				role='link'
				onClick={() => navigate('/account/createSubAccount')}
				disabled={
					!permissions.includes(
						`${permissionName.linkedUser}:${permissionOperation.createUpdate}`
					)
				}
			>
				<Stack direction='row'>
					<AddIcon />
					<Typography sx={{ ...linkText, ml: 1 }}>
						create new sub account
					</Typography>
				</Stack>
			</Button>
			<GridToolbarQuickFilter placeholder='Search...' />
		</GridToolbarContainer>
	);
};

const SubAccountTable = () => {
	const navigate = useNavigate();

	const [filter, setFilter] = useState('');
	const [sortModel, setSortModel] = useState({
		field: 'lastName',
		sort: 'asc',
	});
	const [shouldGrayOut, setShouldGrayOut] = useState(false);
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});

	const clickHandler = subAccount => {
		if (subAccount.ashlinkUserId) {
			navigate('/account/editSubAccounts', {
				state: {
					id: subAccount.ashlinkUserId,
					email: subAccount.email,
					firstName: subAccount.firstName,
					lastName: subAccount.lastName,
					ashlinkUserLevel: subAccount.ashlinkUserLevel,
					termDate: subAccount.termDate,
				},
			});
		}
	};

	const statusMapping = {
		active: {
			icon: <CircleIcon color='success' />,
		},
		termed: {
			icon: <CircleIcon color='error' />,
		},
	};

	const columns = [
		{
			field: 'firstName',
			headerName: 'First Name',
			flex: 1,
			minWidth: 100,
			valueFormatter: value => {
				if (!value) return '';
				return value;
			},
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			flex: 2,
			minWidth: 100,
			valueFormatter: value => {
				if (!value) return '';
				return value;
			},
		},
		{
			field: 'email',
			headerName: 'Email',
			flex: 3,
			minWidth: 100,
			valueFormatter: value => {
				if (!value) return '';
				return value;
			},
		},
		{
			field: 'ashlinkUserLevel',
			headerName: 'Level',
			flex: 1,
			minWidth: 80,
			valueFormatter: value => {
				if (!value) return '';
				return `Level ${value}`;
			},
		},
		{
			field: 'termDate',
			headerName: 'Status',
			minWidth: 150,
			type: 'dateTime',
			valueGetter: value => {
				// Convert string DateTime to Date object for proper sorting
				return value ? new Date(value) : null;
			},
			renderCell: params => {
				const status = params?.row?.termDate ? 'termed' : 'active';
				const { color, icon } = statusMapping[status];
				return (
					<Chip
						variant='outlined'
						label={
							status === 'termed' ? (
								<>
									Termed
									<span style={{ display: 'block' }}>
										{dateToShortString(params.row.termDate)}
									</span>
								</>
							) : (
								'Active'
							)
						}
						color={color}
						icon={icon}
						sx={{
							'&.MuiChip-root': {
								height: 'auto',
							},
						}}
					/>
				);
			},
		},
		{
			field: 'action',
			headerName: 'Action',
			minWidth: 70,
			sortable: false,
			renderCell: params => (
				<Button
					role='link'
					disableRipple
					onClick={() => clickHandler(params?.row)}
					sx={{
						...linkText,
						'&:hover': { backgroundColor: 'transparent' },
					}}
				>
					Edit
				</Button>
			),
		},
	];

	const getSortFieldValue = fieldName => {
		switch (fieldName) {
			case 'firstName':
				return 1;
			case 'lastName':
				return 2;
			case 'email':
				return 3;
			case 'ashlinkUserLevel':
				return 4;
			case 'termDate':
				return 5;
			default:
				return 0;
		}
	};

	const getSortDirection = direction => {
		switch (direction) {
			case 'asc':
				return 1;
			case 'desc':
				return 2;
		}
	};

	// Stable references — creating new objects/arrays on every render causes
	// DataGrid to detect spurious filter/sort changes and fire
	// onPaginationModelChange({ page: 0 }), resetting pagination.
	const filterModelValue = useMemo(
		() => ({
			items: [],
			logicOperator: 'and',
			quickFilterLogicOperator: 'and',
			quickFilterValues: filter ? [filter] : [],
		}),
		[filter]
	);
	const sortModelArray = useMemo(() => [sortModel], [sortModel]);

	const { data, isLoading, isRefetching, refetch, error } = useQuery(
		[
			'SubAccount',
			paginationModel.page,
			filter,
			paginationModel.pageSize,
			sortModel,
		],
		() =>
			GetSubAccountInfoFn(
				paginationModel.page + 1,
				paginationModel.pageSize,
				filter,
				getSortFieldValue(sortModel.field),
				getSortDirection(sortModel.sort)
			), // Page on frontend is 0 indexed, backend API is 1 indexed
		{
			staleTime: Infinity,
			keepPreviousData: true, // Keep previous data while loading new data
		}
	);

	const { ashlinkChildUsers = [], totalRows: totalRowCount } =
		data?.data?.data || {};

	// Keep the last known rowCount so DataGrid never sees rowCount=0 during a
	// page change (which would cause it to reset pagination back to page 0).
	const [rowCountState, setRowCountState] = useState(0);
	useEffect(() => {
		setRowCountState(prev =>
			totalRowCount !== undefined ? totalRowCount : prev
		);
	}, [totalRowCount]);

	// Gray out table only after loading for 1 second
	useEffect(() => {
		let timeoutId;

		if (isLoading || isRefetching) {
			// Start timer when loading begins
			timeoutId = setTimeout(() => {
				setShouldGrayOut(true);
			}, 1000);
		} else {
			// Immediately remove gray out when loading stops
			setShouldGrayOut(false);
		}

		// Cleanup timeout on component unmount or dependency change
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [isLoading, isRefetching]);

	useEffect(() => {
		if (
			sessionStorage.getItem('isCreate') ||
			sessionStorage.getItem('isTerm') ||
			sessionStorage.getItem('isConfirmed')
		) {
			refetch();
		}
	}, []);

	const getGridHeight = () => {
		// If page size is greater than 10, always use fixed height for scrollbar
		if (paginationModel.pageSize > 10) {
			return 739;
		}

		// If page size is 10 or less AND we have 10 or fewer rows, use auto height
		if (ashlinkChildUsers.length <= 10) {
			return 'auto';
		}

		// If page size is 10 but we have more than 10 rows, use fixed height
		return 739;
	};

	return (
		<Box
			sx={{
				height: getGridHeight(),
				width: '100%',
			}}
		>
			<DataGrid
				sx={{
					opacity: shouldGrayOut ? 0.5 : 1,
					transition: 'opacity 0.3s ease',
					pointerEvents: shouldGrayOut ? 'none' : 'auto',
					'& .MuiDataGrid-scrollbarFiller': {
						backgroundColor: 'rgb(0, 46, 110) !important', // eve theme is missing header background color when vertical scrollbar is present
					},
				}}
				showToolbar
				autoHeight={
					paginationModel.pageSize <= 10 &&
					ashlinkChildUsers?.length <= 10
				}
				pagination
				paginationMode='server'
				sortingMode='server'
				filterMode='server'
				rowCount={rowCountState}
				disableColumnMenu
				disableColumnFilter
				disableColumnResize
				disableColumnSelector
				disableDensitySelector
				disableMultipleRowSelection
				hideFooterSelectedRowCount
				getRowId={row =>
					row.ashlinkUserId + row.firstName + row.lastName
				}
				rowHeight={58}
				pageSizeOptions={[10, 20, 50]}
				columns={columns}
				rows={ashlinkChildUsers}
				loading={isLoading || isRefetching}
				paginationModel={paginationModel}
				onPaginationModelChange={newModel => {
					if (newModel.pageSize !== paginationModel.pageSize) {
						setPaginationModel({ ...newModel, page: 0 });
					} else {
						setPaginationModel(newModel);
					}
				}}
				sortModel={sortModelArray}
				onSortModelChange={newSortModel => {
					// DataGrid provides an array; extract the first item (or keep default)
					const sortToApply =
						newSortModel.length > 0
							? newSortModel[0]
							: { field: 'lastName', sort: 'asc' };
					setSortModel(sortToApply);
				}}
				filterModel={filterModelValue}
				onFilterModelChange={newFilterModel => {
					const filterValue =
						newFilterModel.quickFilterValues?.[0] || '';

					if (filterValue !== filter) {
						setFilter(filterValue);
						setPaginationModel(prev => ({ ...prev, page: 0 }));
					}
				}}
				initialState={{
					columns: { columnVisibilityModel: { id: false } },
					sorting: {
						sortModel: [sortModel],
					},
				}}
				slots={{
					noRowsOverlay: CustomNoResultsOverlay,
					loadingOverlay: CustomLoading,
					footer: GridFooter,
					toolbar: CustomToolbar,
				}}
				slotProps={{
					noRowsOverlay: {
						resultCode: error ? 1 : 0,
						subResultCode: error ? 500 : 9001,
						statusCode: error ? 500 : 404,
						customFailureVerbiage: error
							? `Error loading Sub Accounts: ${error.message || 'Please refresh and try again.'}`
							: 'No Sub Accounts',
						customNoResultsVerbiage: 'No Sub Accounts',
					},
					toolbar: {
						showQuickFilter: ashlinkChildUsers?.length > 0,
						quickFilterProps: { debounceMs: 300 },
					},
				}}
			/>
		</Box>
	);
};

export default SubAccountTable;
